import { Validators } from '@angular/forms';
import { FormField, UserRoles } from '../interfaces';

/** ================================= Reusable pieace of form field ================================= */

const firstnameField: FormField = {
    name: 'firstName',
    label: 'First Name',
    placeholder: 'Ex: John',
    type: 'text',
    validators: [Validators.required],
    errors: {
        required: 'First Name is required',
    },
    gridSpan: 'half',
};

const lastNameField: FormField = {
    name: 'lastName',
    label: 'Last Name',
    placeholder: 'Ex: Doe',
    type: 'text',
    validators: [Validators.required],
    errors: {
        required: 'Last Name is required',
    },
    gridSpan: 'half',
};

const contactNumberField: FormField = {
    name: 'contactNumber',
    label: 'Contact Number',
    placeholder: 'Ex: 555 555 1234',
    type: 'text',
    validators: [Validators.required, Validators.pattern(/^[0-9]{10}$/)],
    errors: {
        required: 'Contact Number is required',
        pattern: 'Contact Number must be 10 digits',
    },
    gridSpan: 'half',
};

const contactPersonField: FormField = {
    name: 'contactPerson',
    label: 'Contact Person',
    placeholder: 'Ex: John Doe',
    type: 'text',
    validators: [],
    errors: {},
    gridSpan: 'half',
};

const contactPersonPhoneField: FormField = {
    name: 'contactPersonPhone',
    label: 'Contact Number',
    placeholder: 'Ex: 555 555 1234',
    type: 'text',
    validators: [Validators.pattern(/^[0-9]{10}$/)],
    errors: {
        pattern: 'Contact Number must be 10 digits',
    },
    gridSpan: 'half',
};

/** Requires a normal domain with TLD (Angular's email validator accepts some invalid hostnames). */
const EMAIL_WITH_TLD_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const emailField: FormField = {
    name: 'emailAddress',
    label: 'Email Address',
    placeholder: 'Ex: user@name.com',
    type: 'email',
    validators: [Validators.required, Validators.pattern(EMAIL_WITH_TLD_PATTERN)],
    errors: {
        required: 'Email Address is required',
        pattern: 'Enter a valid email address',
        emailTaken: 'Email Address is already taken',
    },
    gridSpan: 'quarter',
};

const cityField: FormField = {
    name: 'city',
    label: 'City',
    placeholder: 'Type a city or state (min: 3-4 characters)',
    type: 'autocomplete',
    validators: [Validators.required],
    errors: {
        required: 'City is required',
    },
    gridSpan: 'quarter',
};

const stateField: FormField = {
    name: 'state',
    label: 'State',
    placeholder: 'Select a City',
    type: 'text',
    validators: [],
    errors: {},
    gridSpan: 'quarter',
    disabled: true,
};

const regionField: FormField = {
    name: 'region',
    label: 'Region',
    placeholder: 'Select a City',
    type: 'text',
    validators: [],
    errors: {},
    gridSpan: 'quarter',
    disabled: true,
};

/**
 * Password validation pattern
 * Requires: lowercase, uppercase, number, and at least one special character (any non-alphanumeric)
 * Minimum length: 8 characters, Maximum length: 72 characters
 */
export const PASSWORD_PATTERN =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d])[A-Za-z\d\S]{8,72}$/;

const passwordField: FormField = {
    name: 'password',
    label: 'Password',
    placeholder: 'Note: Minimum of 8 characters',
    type: 'password',
    validators: [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(72),
        Validators.pattern(PASSWORD_PATTERN),
    ],
    errors: {
        required: 'Password is required',
        minlength: 'Password must be at least 8 characters',
        maxlength: 'Password must not exceed 72 characters',
        pattern: 'Password must include uppercase, lowercase, number, and special character',
    },
    gridSpan: 'half',
};

const confirmPasswordField: FormField = {
    name: 'passwordConfirmation',
    label: 'Confirm Password',
    placeholder: 'Note: Must match entered password',
    type: 'password',
    validators: [
        Validators.required,
        Validators.maxLength(72),
        Validators.pattern(PASSWORD_PATTERN),
    ],
    errors: {
        passwordsDoNotMatch: 'Passwords do not match',
        required: 'Confirm Password is required',
        maxlength: 'Password must not exceed 72 characters',
        pattern: 'Password must include uppercase, lowercase, number, and special character',
    },
    gridSpan: 'half',
};

/** ==================================== END ======================================= */

export const DEALER_INPUT_INFORMATION_FORM_FIELDS: FormField[] = [
    firstnameField,
    lastNameField,
    {
        name: 'dealerId',
        label: 'Dealer ID',
        placeholder: 'Ex: D.001',
        type: 'text',
        validators: [Validators.required],
        errors: {
            required: 'Dealer ID is required',
        },
        gridSpan: 'quarter',
    },
    {
        name: 'dealerAlias',
        label: 'Dealer Alias',
        placeholder: 'Ex: NCMPS-BLUEIGUANA-9921',
        type: 'text',
        validators: [Validators.required],
        errors: {
            required: 'Dealer Alias is required',
        },
        gridSpan: 'quarter',
    },
    {
        name: 'businessName',
        label: 'Business Name',
        placeholder: 'Ex: Blue Iguana',
        type: 'text',
        validators: [Validators.required],
        errors: {
            required: 'Business Name is required',
        },
        gridSpan: 'half',
    },
    contactPersonField,
    contactPersonPhoneField,
    emailField,
    cityField,
    stateField,
    regionField,
    passwordField,
    confirmPasswordField,
];

/**
 * Dealer edit modal / single-dealer: no password fields.
 * Email + reset row is last so city, state, and region stay on one grid row (city uses half width).
 */
export const DEALER_EDIT_INFORMATION_FORM_FIELDS: FormField[] = (() => {
    const fields = DEALER_INPUT_INFORMATION_FORM_FIELDS.filter(
        (f) => f.name !== 'password' && f.name !== 'passwordConfirmation',
    );
    const email = fields.find((f) => f.name === 'emailAddress');
    const withoutEmail = fields.filter((f) => f.name !== 'emailAddress');
    if (!email) {
        return fields;
    }
    return [
        ...withoutEmail.map((f) => (f.name === 'city' ? { ...f, gridSpan: 'half' as const } : f)),
        email,
    ];
})();

/**
 * Used by all other roles but dealer
 */
export const INPUT_INFORMATION_FORM_FIELDS: FormField[] = [
    firstnameField,
    lastNameField,
    contactNumberField,
    {
        ...emailField,
        gridSpan: 'half',
    },
    passwordField,
    confirmPasswordField,
];

/**
 * INPUT INFORMATION STEP
 *
 * Layout for all the user roles to be created in the input information step
 */
export const INPUT_INFORMATION_STEP_LAYOUT: Record<Exclude<UserRoles, 'none'>, FormField[]> = {
    superAdmin: INPUT_INFORMATION_FORM_FIELDS,
    dealer: DEALER_INPUT_INFORMATION_FORM_FIELDS,
    subDealer: INPUT_INFORMATION_FORM_FIELDS,
    dealerAdmin: INPUT_INFORMATION_FORM_FIELDS,
    techOfficer: INPUT_INFORMATION_FORM_FIELDS,
    advertiser: INPUT_INFORMATION_FORM_FIELDS,
    guest: INPUT_INFORMATION_FORM_FIELDS,
};
